import { Moment } from 'moment';

import {
  BOOKING_TYPES,
  CANCELLED_STATUSES,
  ORDER_PENDING_STAGES,
  POTENTIAL_PENDING_STAGES,
} from '../constants/bookings';
import { RECURRING_CHILDCARE } from '../constants/prestations';
import {
  Booking,
  BookingPreview,
  ChildcareOrder,
  ChildcarePotential,
  ChildcareRecurringOrder,
  ChildcareRecurringPotential,
  CleaningOrder,
  CleaningPotential,
  CleaningRecurringOrder,
  CleaningRecurringPotential,
  Order,
  OrderPreview,
  Potential,
  PotentialPreview,
  WellnessOrder,
  WellnessPotential,
} from '../types/customer';

import * as universes from './universes';
import { isChildcareReference, isCleaningReference, isWellnessReference } from './universes';
import moment from './moment';

// TODO TI Booking: Create isOrderPreview utils
export const isOrder = (booking: Booking | BookingPreview): booking is Order | OrderPreview =>
  booking.type === BOOKING_TYPES.ORDER;

// TODO TI Booking: Create isPotentialPreview utils
export const isPotential = (booking: Booking | BookingPreview): booking is Potential | PotentialPreview =>
  booking.type === BOOKING_TYPES.POTENTIAL;

export const isRecurringCleaning = (
  booking: Booking | BookingPreview,
): booking is CleaningRecurringOrder | CleaningRecurringPotential =>
  universes.isCleaningReference(booking.universe) && booking.prestations_with_options[0].prestation.is_recurrent;

export const isRecurringChildcare = (
  booking?: Booking | BookingPreview | null,
): booking is ChildcareRecurringOrder | ChildcareRecurringPotential => {
  if (!booking) return false;

  const { prestations } = booking;
  return prestations && prestations.length > 0 && prestations[0].includes(RECURRING_CHILDCARE);
};

export const isRecurring = (
  booking?: Booking | BookingPreview | null,
): booking is
  | CleaningRecurringOrder
  | CleaningRecurringPotential
  | ChildcareRecurringOrder
  | ChildcareRecurringPotential => {
  if (!booking) return false;

  return isRecurringCleaning(booking) || isRecurringChildcare(booking);
};

export const isChildcareBooking = (
  booking?: Booking | BookingPreview | null,
): booking is ChildcarePotential | ChildcareOrder => {
  if (!booking) return false;

  return isChildcareReference(booking.universe);
};

export const isWellnessBooking = (
  booking?: Booking | BookingPreview | null,
): booking is WellnessPotential | WellnessOrder => {
  if (!booking) return false;

  return isWellnessReference(booking.universe);
};

export const isCleaningBooking = (
  booking?: Booking | BookingPreview | null,
): booking is CleaningPotential | CleaningOrder => {
  if (!booking) return false;

  return isCleaningReference(booking.universe);
};

export const isChildcareFreeMeeting = (order?: Order | OrderPreview | null) => {
  if (!order) return false;

  const { universe, free_meeting: isFreeMeeting } = order;

  return isChildcareReference(universe) && isFreeMeeting;
};

/**
 * Returns a boolean indicating whether a booking may have counter proposals or not.
 * Stage 'Received' is tested in case booking switched to 'Proposed' stage but has
 * not been fetched again yet.
 */
export const mayHaveCounterProposals = (booking: Booking): booking is Potential =>
  isPotential(booking) && (booking.stage === 'Received' || booking.stage === 'Proposed');

export const isPending = (booking: Booking) =>
  (isOrder(booking) && ORDER_PENDING_STAGES.includes(booking.stage)) ||
  (isPotential(booking) && POTENTIAL_PENDING_STAGES.includes(booking.stage));

export const isNotPending = (booking: Booking) => !isPending(booking);

export const isOrderOngoing = (booking: Booking) => {
  const { appointment, stage } = booking;
  const now = moment();
  const appointmentDate = moment(appointment);

  return isOrder(booking) && stage === 'Accepted' && now.isSameOrAfter(appointmentDate);
};

export const isCancelled = ({ stage }: Booking | BookingPreview) => CANCELLED_STATUSES.includes(stage);

export const getCreatedAt = (booking?: Booking): Moment => {
  if (!booking) return moment();
  // When a pro change or a pro replacement was performed by the customer, a new potential will be created.
  // As we want to keep the prices that were in vigor when the original potential was created, we need to use
  // price_date_at. price_date_at can be defined also for other use cases.
  if (isOrder(booking)) return moment(booking.potential?.price_date_at || booking.potential?.created_at);

  return moment(booking.price_date_at || booking.created_at);
};

export const isPossibleToModify = (booking: Booking) => {
  if (isOrder(booking)) {
    return booking.change_request_possible;
  }

  return booking.change_possible;
};

export const shouldUseRecurrenceOrders = (
  booking: Booking,
): booking is CleaningRecurringOrder | ChildcareRecurringOrder => {
  if (!isOrder(booking)) return false;
  if (!isRecurring(booking)) return false;

  const { recurrence_orders: recurrenceOrders } = booking;

  return !!recurrenceOrders?.length;
};

export const isFrequencyScreenAccessible = (booking: Booking, updateAllOccurrences: boolean) => {
  if (booking.type === 'potential') return isRecurringCleaning(booking);
  const isMultipleTimesAWeek = !!booking.weekly_recurrence && booking.weekly_recurrence > 1;
  return isRecurringCleaning(booking) && updateAllOccurrences && !isMultipleTimesAWeek;
};

export const hasOptionInBooking = (booking: Booking, optionReference: string) =>
  booking.prestations_with_options.length > 0 &&
  booking.prestations_with_options.some((prestation) =>
    prestation.options.some((option) => option.reference === optionReference),
  );

export const isRecurrentOrderWithRecurrences = (
  booking: Booking,
): booking is CleaningRecurringOrder | ChildcareRecurringOrder =>
  isOrder(booking) &&
  isRecurring(booking) &&
  shouldUseRecurrenceOrders(booking) &&
  !!booking.recurrence_orders &&
  booking.recurrence_orders.length > 0;
